import React, { useState, useRef, Children, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, FileText, CheckCircle2, Download, ChevronDown } from 'lucide-react';
import { PricingCard } from '../components/PricingCard';
import jsPDF from 'jspdf';
const PRICING_DATA = {
  1: {
    single: {
      total: 150
    },
    pack4: {
      total: 550
    },
    pack8: {
      total: 1050
    },
    pack12: {
      total: 1450
    }
  },
  2: {
    single: {
      total: 200,
      perPerson: 100
    },
    pack4: {
      total: 760,
      perPerson: 380
    },
    pack8: {
      total: 1400,
      perPerson: 700
    },
    pack12: {
      total: 2000,
      perPerson: 1000
    }
  },
  3: {
    single: {
      total: 270,
      perPerson: 90
    },
    pack4: {
      total: 990,
      perPerson: 330
    },
    pack8: {
      total: 1890,
      perPerson: 630
    },
    pack12: {
      total: 2610,
      perPerson: 870
    }
  },
  4: {
    single: {
      total: 320,
      perPerson: 80
    },
    pack4: {
      total: 1160,
      perPerson: 290
    },
    pack8: {
      total: 2240,
      perPerson: 560
    },
    pack12: {
      total: 3080,
      perPerson: 770
    }
  }
};
const BASE_FEATURES = [{
  name: 'Technique assessment',
  included: true
}, {
  name: 'Personalized feedback',
  included: true
}];
const SINGLE_FEATURES = [...BASE_FEATURES, {
  name: 'Custom training plan',
  included: true
}, {
  name: 'Progress tracking',
  included: false
}];
const PACK4_FEATURES = [...BASE_FEATURES, {
  name: 'Custom training plan',
  included: true
}, {
  name: 'Progress tracking',
  included: false
}];
const PACK8_FEATURES = [...BASE_FEATURES, {
  name: 'Custom training plan',
  included: true
}, {
  name: 'Progress tracking',
  included: true
}];
const PACK12_FEATURES = [...BASE_FEATURES, {
  name: 'Custom training plan',
  included: true
}, {
  name: 'Progress tracking',
  included: true
}];
export function CoachingPage() {
  const [groupSize, setGroupSize] = useState<1 | 2 | 3 | 4>(1);
  const [waiverSigned, setWaiverSigned] = useState(false);
  const [mediaConsent, setMediaConsent] = useState<'agree' | 'disagree' | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientDate, setClientDate] = useState('');
  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let y = 20;
    const addText = (text: string, size: number, style: string = 'normal', align: string = 'left') => {
      doc.setFontSize(size);
      doc.setFont('helvetica', style);
      const lines = doc.splitTextToSize(text, maxWidth);
      if (y + lines.length * (size * 0.5) > 270) {
        doc.addPage();
        y = 20;
      }
      if (align === 'center') {
        lines.forEach((line: string) => {
          const lineWidth = doc.getTextWidth(line);
          doc.text(line, (pageWidth - lineWidth) / 2, y);
          y += size * 0.5;
        });
      } else {
        doc.text(lines, margin, y);
        y += lines.length * (size * 0.5);
      }
    };
    const addSpace = (px: number) => {
      y += px;
    };
    const checkNewPage = (needed: number) => {
      if (y + needed > 270) {
        doc.addPage();
        y = 20;
      }
    };
    // Title
    addText('TRAINING AGREEMENT, RELEASE OF LIABILITY', 14, 'bold', 'center');
    addText('& ASSUMPTION OF RISK', 14, 'bold', 'center');
    addSpace(8);
    // Client info
    addText(`Client Name: ${clientName}`, 11, 'normal');
    addText(`Date: ${clientDate}`, 11, 'normal');
    addSpace(8);
    // Section I
    addText('I. RELEASE OF LIABILITY & ASSUMPTION OF RISK', 12, 'bold');
    addSpace(4);
    addText('Nature of Activity', 10, 'bold');
    addText('I, the undersigned, acknowledge that kickboxing, boxing, and physical conditioning are high-intensity activities involving physical exertion, striking movements, and contact with equipment and/or training partners.', 9, 'normal');
    addSpace(4);
    addText('Assumption of Risk', 10, 'bold');
    addText('I voluntarily assume all risks of injury, whether known or unknown, that may arise from training or the use of gym equipment (including but not limited to: heavy bags, pads, mats, wall-mounted bars, rollers, and other training tools). These risks include, but are not limited to:', 9, 'normal');
    addSpace(2);
    const risks = ['• Muscle strains, ligament tears, or joint injuries', '• Cuts, scrapes, swelling, or bruising', '• Physical exhaustion, dehydration, or dizziness', '• Contact-related injuries', '• Any other physical or medical injury resulting from intense exercise or equipment use'];
    risks.forEach(r => addText(r, 9, 'normal'));
    addSpace(4);
    addText('Release of Liability', 10, 'bold');
    addText('To the fullest extent permitted by applicable law, I hereby release, waive, and discharge Aleks Szulc ("the Coach") from any and all liability, claims, or demands for personal injury or property damage arising out of my participation in training sessions. This release does not apply to damages resulting from gross negligence or intentional misconduct.', 9, 'normal');
    addSpace(4);
    addText('Indemnification', 10, 'bold');
    addText('I agree to hold the Coach harmless from any loss, liability, or costs (including legal fees) incurred as a result of any claim or lawsuit brought by me or on my behalf related to my participation.', 9, 'normal');
    addSpace(4);
    addText('Medical Fitness & Responsibility', 10, 'bold');
    addText('I confirm that I am in good physical health and capable of participating in intense physical activity. I have disclosed any relevant medical conditions. I understand that if I have any doubts, I should consult a physician before training. I agree to immediately stop training and inform the Coach if I feel pain, discomfort, dizziness, or any unusual symptoms.', 9, 'normal');
    addSpace(4);
    addText('Emergency Medical Consent', 10, 'bold');
    addText('In the event of injury or medical emergency, I authorize the Coach to seek medical assistance on my behalf, including contacting emergency services. I acknowledge that I am responsible for any medical costs incurred.', 9, 'normal');
    addSpace(8);
    // Section II
    checkNewPage(60);
    addText('II. TRAINING CONDITIONS & CONDUCT', 12, 'bold');
    addSpace(4);
    addText('Compliance with Instructions', 10, 'bold');
    addText('I agree to follow all safety instructions and guidance provided by the Coach. I understand that failure to do so increases my risk of injury and may result in termination of the session.', 9, 'normal');
    addSpace(4);
    addText('Training Environment', 10, 'bold');
    addText('I understand that sessions may take place in gyms, outdoor areas, or other training environments, and I accept all risks associated with these locations.', 9, 'normal');
    addSpace(4);
    addText('Conduct', 10, 'bold');
    addText('The Coach reserves the right to terminate a session or package (without refund) if I display unsafe, aggressive, or inappropriate behavior, or fail to follow instructions.', 9, 'normal');
    addSpace(8);
    // Section III
    checkNewPage(80);
    addText('III. PAYMENT & PACKAGE POLICY', 12, 'bold');
    addSpace(4);
    addText('No-Refund Policy', 10, 'bold');
    addText('All payments for individual sessions and training packages are final and non-refundable. By purchasing a package, the Client acknowledges that they are committing to the full value of the services.', 9, 'normal');
    addSpace(2);
    addText('Refunds will not be issued except where required by applicable law or in limited exceptional circumstances, such as serious injury preventing participation (supported by valid medical documentation). In such cases, any refund, partial refund, or credit remains solely at the reasonable discretion of the Coach, evaluated on a case-by-case basis.', 9, 'normal');
    addSpace(2);
    addText('The Coach reserves the right, but is not obligated, to offer a refund, partial refund, or alternative solution in specific situations where it is deemed appropriate.', 9, 'normal');
    addSpace(4);
    addText('No Expiration', 10, 'bold');
    addText('Purchased training sessions do not expire and remain valid until used.', 9, 'normal');
    addSpace(4);
    addText('Non-Transferable', 10, 'bold');
    addText('All sessions are assigned to the registered Client only and cannot be transferred, gifted, or sold.', 9, 'normal');
    addSpace(8);
    // Section IV
    checkNewPage(40);
    addText('IV. CANCELLATION POLICY', 12, 'bold');
    addSpace(4);
    addText('24-Hour Notice Requirement', 10, 'bold');
    addText('Cancellations or rescheduling must be made at least 24 hours in advance. Failure to provide sufficient notice will result in the session being forfeited and deducted from the package balance.', 9, 'normal');
    addSpace(8);
    // Section V
    checkNewPage(50);
    addText('V. DATA PROTECTION (GDPR)', 12, 'bold');
    addSpace(4);
    addText('I acknowledge that Aleks Szulc processes my personal data (such as name and contact details) solely for the purpose of providing training services, scheduling sessions, and communication.', 9, 'normal');
    addSpace(2);
    addText('I understand that:', 9, 'normal');
    addText('• My data will not be shared with third parties without my consent, unless required by law', 9, 'normal');
    addText('• I have the right to access, correct, or request deletion of my data', 9, 'normal');
    addText('• I may withdraw consent at any time by contacting the Coach', 9, 'normal');
    addSpace(8);
    // Section VI
    checkNewPage(40);
    addText('VI. MEDIA RELEASE (OPTIONAL)', 12, 'bold');
    addSpace(4);
    addText('I grant permission for the Coach to take photos and videos during training sessions and use them for promotional purposes (e.g., social media, website), unless I explicitly withdraw this consent in writing.', 9, 'normal');
    addSpace(4);
    addText(`[${mediaConsent === 'agree' ? 'X' : ' '}] I agree          [${mediaConsent === 'disagree' ? 'X' : ' '}] I do NOT agree`, 10, 'bold');
    addSpace(8);
    // Section VII
    checkNewPage(30);
    addText('VII. GOVERNING LAW', 12, 'bold');
    addSpace(4);
    addText('This Agreement shall be governed by and interpreted in accordance with the laws of Poland.', 9, 'normal');
    addSpace(12);
    // Acknowledgment
    checkNewPage(60);
    addText('ACKNOWLEDGMENT', 12, 'bold', 'center');
    addSpace(4);
    addText('I HAVE READ THIS AGREEMENT, FULLY UNDERSTAND ITS TERMS, AND VOLUNTARILY SIGN IT.', 10, 'bold', 'center');
    addSpace(16);
    // Signatures
    addText('Participant Signature:', 10, 'bold');
    addSpace(2);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(14);
    doc.text(clientName, margin, y);
    y += 4;
    doc.setDrawColor(0);
    doc.line(margin, y, margin + 80, y);
    addSpace(4);
    addText('(Parent/Guardian if under 18)', 8, 'italic');
    addSpace(12);
    addText('Coach Signature:', 10, 'bold');
    addSpace(2);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(14);
    doc.text('Aleks Szulc', margin, y);
    y += 4;
    doc.setDrawColor(0);
    doc.line(margin, y, margin + 80, y);
    // Use blob approach for better Android compatibility
    const pdfBlob = doc.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    const fileName = `Training_Agreement_${clientName.replace(/\s+/g, '_')}.pdf`;
    // Try using a temporary link element (works on most browsers including Android)
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    // Cleanup after a short delay to ensure download starts
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, 500);
  };
  const waiverRef = useRef<HTMLDivElement>(null);
  const handleWaiverSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mediaConsent === null) {
      alert('Please select your media release preference.');
      return;
    }
    setWaiverSigned(true);
    setTimeout(() => {
      waiverRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 100);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };
  const currentPricing = PRICING_DATA[groupSize];
  const whatsappLink = (plan: string, price: number) => {
    const groupLabel = groupSize === 1 ? '1 person (solo)' : `${groupSize} people`;
    const message = `Hi Aleks! I'm interested in the ${plan} (${price} PLN) for ${groupLabel}. I'd like to book!`;
    return `https://wa.me/48536052998?text=${encodeURIComponent(message)}`;
  };
  const trialWhatsappLink = `https://wa.me/48536052998?text=${encodeURIComponent("Hi Aleks! I'm interested in a Trial Session (50 PLN). I'd like to book!")}`;
  return <main className="min-h-screen pt-32 pb-20 px-6" data-id="element-144">
      <div className="max-w-7xl mx-auto" data-id="element-145">
        {/* Hero Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-10" data-id="element-146">
          <h1 className="text-5xl md:text-7xl font-heading text-white mb-4" data-id="element-147">
            TRAIN WITH ALEKS
          </h1>
          <p className="max-w-2xl mx-auto text-gray-400 font-body" data-id="element-148">
            Train with a National Champion. Whether you're stepping on to the
            mat for the first time or preparing for competition, get elite-level
            coaching tailored to your goals.
          </p>
        </motion.div>

        {/* Trial Session (Standalone) */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="max-w-md mx-auto mb-16" data-id="element-149">
          <PricingCard title="THE ENTRY" price="50 PLN" description="Your first step on to the mat. Trial session." features={[...BASE_FEATURES, {
          name: 'Custom training plan',
          included: false
        }, {
          name: 'Progress tracking',
          included: false
        }]} ctaText="BOOK TRIAL" ctaLink={trialWhatsappLink} data-id="element-150" />
        </motion.div>

        {/* Group Size Tabs */}
        <div className="max-w-3xl mx-auto mb-12" data-id="element-151">
          <div className="flex p-1 bg-dark-elevated border border-dark-border rounded-xl" data-id="element-152">
            {[1, 2, 3, 4].map(size => <button key={size} onClick={() => setGroupSize(size as 1 | 2 | 3 | 4)} className={`relative flex-1 py-3 text-sm md:text-base font-heading tracking-wider rounded-lg transition-colors z-10 ${groupSize === size ? 'text-black' : 'text-gray-400 hover:text-white'}`} data-id="element-153">
                {groupSize === size && <motion.div layoutId="activeTab" className="absolute inset-0 bg-accent-blue rounded-lg -z-10" transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-154" />}
                {size === 1 ? '1 Person (Solo)' : `${size} People`}
              </button>)}
          </div>
        </div>

        {/* Pricing Grid */}
        <motion.div key={groupSize} variants={containerVariants} initial="hidden" animate="show" className="flex lg:grid lg:grid-cols-4 gap-6 lg:gap-6 items-center mb-4 lg:mb-12 overflow-x-auto snap-x snap-mandatory pt-6 pb-4 lg:pt-0 lg:pb-0 lg:overflow-visible scrollbar-hide" data-id="element-155">
          <motion.div variants={itemVariants} className="h-full min-w-[280px] snap-center shrink-0 lg:min-w-0 lg:shrink" data-id="element-156">
            <PricingCard title="SINGLE SESSION" price={`${currentPricing.single.total} PLN`} subtitle={groupSize > 1 ? `${currentPricing.single.perPerson} PLN per person` : undefined} description="Train on your schedule. Pay as you go." features={SINGLE_FEATURES} ctaText="BOOK SESSION" ctaLink={whatsappLink('Single Session', currentPricing.single.total)} data-id="element-157" />
          </motion.div>

          <motion.div variants={itemVariants} className="h-full min-w-[280px] snap-center shrink-0 lg:min-w-0 lg:shrink" data-id="element-158">
            <PricingCard title="THE STARTER" price={`${currentPricing.pack4.total} PLN`} subtitle={groupSize > 1 ? `${currentPricing.pack4.perPerson} PLN per person` : undefined} description={`Pack of 4 sessions (${Math.round(currentPricing.pack4.total / 4)} PLN/session).`} features={PACK4_FEATURES} ctaText="GET 4-PACK" ctaLink={whatsappLink('4-Pack (The Starter)', currentPricing.pack4.total)} data-id="element-159" />
          </motion.div>

          <motion.div variants={itemVariants} className="h-full lg:scale-105 z-10 min-w-[280px] snap-center shrink-0 lg:min-w-0 lg:shrink" data-id="element-160">
            <PricingCard title="THE COMMITMENT" price={`${currentPricing.pack8.total} PLN`} subtitle={groupSize > 1 ? `${currentPricing.pack8.perPerson} PLN per person` : undefined} description={`Pack of 8 sessions (${Math.round(currentPricing.pack8.total / 8)} PLN/session).`} features={PACK8_FEATURES} isPopular={true} isLarge={true} badgeText="MOST POPULAR" ctaText="GET 8-PACK" ctaLink={whatsappLink('8-Pack (The Commitment)', currentPricing.pack8.total)} data-id="element-161" />
          </motion.div>

          <motion.div variants={itemVariants} className="h-full lg:scale-105 z-10 min-w-[280px] snap-center shrink-0 lg:min-w-0 lg:shrink" data-id="element-162">
            <PricingCard title="THE PRO" price={`${currentPricing.pack12.total} PLN`} subtitle={groupSize > 1 ? `${currentPricing.pack12.perPerson} PLN per person` : undefined} description={`Pack of 12 sessions (${Math.round(currentPricing.pack12.total / 12)} PLN/session).`} features={PACK12_FEATURES} isPopular={true} isLarge={true} badgeText="BEST VALUE" ctaText="GET 12-PACK" ctaLink={whatsappLink('12-Pack (The Pro)', currentPricing.pack12.total)} data-id="element-163" />
          </motion.div>
        </motion.div>

        {/* Swipe Indicator - mobile only */}
        <div className="flex lg:hidden items-center justify-center gap-2 mb-24" data-id="element-164">
          <div className="flex items-center gap-1.5 text-gray-500 text-sm font-body" data-id="element-165">
            <svg className="w-4 h-4 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-166">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" data-id="element-167" />
            </svg>
            <span data-id="element-168">Swipe to see all plans</span>
            <svg className="w-4 h-4 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-169">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-170" />
            </svg>
          </div>
        </div>

        {/* Match & Strike Program */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-24" data-id="element-171">
          <div className="max-w-3xl mx-auto bg-dark-card border border-dark-border rounded-2xl p-8 md:p-12 text-center" data-id="element-172">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-173">
              <svg className="w-8 h-8 text-accent-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} data-id="element-174">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" data-id="element-175" />
              </svg>
              <h2 className="text-3xl font-heading text-white" data-id="element-176">
                THE MATCH & STRIKE PROGRAM
              </h2>
            </div>
            <p className="text-gray-400 font-body mb-3 max-w-xl mx-auto" data-id="element-177">
              Want to train but looking for a more affordable option? Group
              sessions are cheaper per person — but you might not know anyone to
              train with.
            </p>
            <p className="text-gray-400 font-body mb-8 max-w-xl mx-auto" data-id="element-178">
              That's where{' '}
              <span className="text-white font-semibold" data-id="element-179">Match & Strike</span>{' '}
              comes in. Fill out the form below and I'll match you with other
              athletes based on your goals, schedule, and experience level.
              Train together, save money, and push each other.
            </p>
            <a href="https://forms.gle/1Y2menayGYXYLmcq5" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-accent-blue text-black font-heading tracking-wider text-lg px-10 py-4 rounded-lg hover:bg-accent-blue-hover transition-colors" data-id="element-180">
              FIND YOUR TRAINING PARTNER
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-181">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" data-id="element-182" />
              </svg>
            </a>
          </div>
        </motion.div>

        {/* Contract & Waiver Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-24" ref={waiverRef} data-id="element-183">
          <div className="max-w-3xl mx-auto" data-id="element-184">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-185">
              <FileText className="w-7 h-7 text-accent-blue" data-id="element-186" />
              <h2 className="text-3xl font-heading text-white text-center" data-id="element-187">
                CONTRACT & WAIVER
              </h2>
            </div>
            <p className="text-gray-400 font-body text-center mb-10" data-id="element-188">
              Please read and sign the agreement below before your first
              session.
            </p>

            <div className="bg-dark-card border border-dark-border rounded-2xl p-8" data-id="element-189">
              {waiverSigned ? <div className="flex flex-col items-center justify-center py-12 text-center space-y-4" data-id="element-190">
                  <CheckCircle2 className="w-16 h-16 text-accent-blue" data-id="element-191" />
                  <h3 className="text-3xl font-heading text-white" data-id="element-192">
                    AGREEMENT SIGNED
                  </h3>
                  <p className="text-gray-400 font-body" data-id="element-193">
                    Thank you for signing. Please download your contract below
                    and send it to Aleks to complete your registration.
                  </p>
                  <button onClick={generatePDF} className="flex items-center gap-3 bg-accent-blue text-black font-heading tracking-wider text-lg px-8 py-4 rounded-lg hover:bg-accent-blue-hover transition-colors mt-4" data-id="element-194">
                    <Download className="w-5 h-5" data-id="element-195" />
                    DOWNLOAD SIGNED CONTRACT
                  </button>
                  <a href="/#/contact" className="flex items-center justify-center gap-3 border border-accent-blue text-accent-blue font-heading tracking-wider text-lg px-8 py-4 rounded-lg hover:bg-accent-blue hover:text-black transition-colors" data-id="element-196">
                    CONTACT ALEKS
                  </a>
                </div> : <>
                  {/* Contract Text */}
                  <div className="bg-dark-primary border border-dark-border rounded-xl p-6 mb-8 max-h-96 overflow-y-auto" data-id="element-197">
                    <h3 className="text-xl font-heading text-white mb-6 text-center" data-id="element-198">
                      TRAINING AGREEMENT, RELEASE OF LIABILITY & ASSUMPTION OF
                      RISK
                    </h3>

                    <div className="text-sm text-gray-400 font-body space-y-6" data-id="element-199">
                      {/* Section I */}
                      <div data-id="element-200">
                        <h4 className="text-base font-heading text-accent-blue mb-3" data-id="element-201">
                          I. RELEASE OF LIABILITY & ASSUMPTION OF RISK
                        </h4>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-202">
                          Nature of Activity
                        </p>
                        <p className="mb-3" data-id="element-203">
                          I, the undersigned, acknowledge that kickboxing,
                          boxing, and physical conditioning are high-intensity
                          activities involving physical exertion, striking
                          movements, and contact with equipment and/or training
                          partners.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-204">
                          Assumption of Risk
                        </p>
                        <p className="mb-2" data-id="element-205">
                          I voluntarily assume all risks of injury, whether
                          known or unknown, that may arise from training or the
                          use of gym equipment (including but not limited to:
                          heavy bags, pads, mats, wall-mounted bars, rollers,
                          and other training tools). These risks include, but
                          are not limited to:
                        </p>
                        <ul className="list-disc list-inside space-y-1 mb-3 ml-2" data-id="element-206">
                          <li data-id="element-207">
                            Muscle strains, ligament tears, or joint injuries
                          </li>
                          <li data-id="element-208">Cuts, scrapes, swelling, or bruising</li>
                          <li data-id="element-209">
                            Physical exhaustion, dehydration, or dizziness
                          </li>
                          <li data-id="element-210">Contact-related injuries</li>
                          <li data-id="element-211">
                            Any other physical or medical injury resulting from
                            intense exercise or equipment use
                          </li>
                        </ul>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-212">
                          Release of Liability
                        </p>
                        <p className="mb-3" data-id="element-213">
                          To the fullest extent permitted by applicable law, I
                          hereby release, waive, and discharge Aleks Szulc ("the
                          Coach") from any and all liability, claims, or demands
                          for personal injury or property damage arising out of
                          my participation in training sessions. This release
                          does not apply to damages resulting from gross
                          negligence or intentional misconduct.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-214">
                          Indemnification
                        </p>
                        <p className="mb-3" data-id="element-215">
                          I agree to hold the Coach harmless from any loss,
                          liability, or costs (including legal fees) incurred as
                          a result of any claim or lawsuit brought by me or on
                          my behalf related to my participation.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-216">
                          Medical Fitness & Responsibility
                        </p>
                        <p className="mb-3" data-id="element-217">
                          I confirm that I am in good physical health and
                          capable of participating in intense physical activity.
                          I have disclosed any relevant medical conditions. I
                          understand that if I have any doubts, I should consult
                          a physician before training. I agree to immediately
                          stop training and inform the Coach if I feel pain,
                          discomfort, dizziness, or any unusual symptoms.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-218">
                          Emergency Medical Consent
                        </p>
                        <p data-id="element-219">
                          In the event of injury or medical emergency, I
                          authorize the Coach to seek medical assistance on my
                          behalf, including contacting emergency services. I
                          acknowledge that I am responsible for any medical
                          costs incurred.
                        </p>
                      </div>

                      {/* Section II */}
                      <div data-id="element-220">
                        <h4 className="text-base font-heading text-accent-blue mb-3" data-id="element-221">
                          II. TRAINING CONDITIONS & CONDUCT
                        </h4>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-222">
                          Compliance with Instructions
                        </p>
                        <p className="mb-3" data-id="element-223">
                          I agree to follow all safety instructions and guidance
                          provided by the Coach. I understand that failure to do
                          so increases my risk of injury and may result in
                          termination of the session.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-224">
                          Training Environment
                        </p>
                        <p className="mb-3" data-id="element-225">
                          I understand that sessions may take place in gyms,
                          outdoor areas, or other training environments, and I
                          accept all risks associated with these locations.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-226">
                          Conduct
                        </p>
                        <p data-id="element-227">
                          The Coach reserves the right to terminate a session or
                          package (without refund) if I display unsafe,
                          aggressive, or inappropriate behavior, or fail to
                          follow instructions.
                        </p>
                      </div>

                      {/* Section III */}
                      <div data-id="element-228">
                        <h4 className="text-base font-heading text-accent-blue mb-3" data-id="element-229">
                          III. PAYMENT & PACKAGE POLICY
                        </h4>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-230">
                          No-Refund Policy
                        </p>
                        <p className="mb-3" data-id="element-231">
                          All payments for individual sessions and training
                          packages are final and non-refundable. By purchasing a
                          package, the Client acknowledges that they are
                          committing to the full value of the services.
                        </p>
                        <p className="mb-3" data-id="element-232">
                          Refunds will not be issued except where required by
                          applicable law or in limited exceptional
                          circumstances, such as serious injury preventing
                          participation (supported by valid medical
                          documentation). In such cases, any refund, partial
                          refund, or credit remains solely at the reasonable
                          discretion of the Coach, evaluated on a case-by-case
                          basis.
                        </p>
                        <p className="mb-3" data-id="element-233">
                          The Coach reserves the right, but is not obligated, to
                          offer a refund, partial refund, or alternative
                          solution in specific situations where it is deemed
                          appropriate.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-234">
                          No Expiration
                        </p>
                        <p className="mb-3" data-id="element-235">
                          Purchased training sessions do not expire and remain
                          valid until used.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-236">
                          Non-Transferable
                        </p>
                        <p data-id="element-237">
                          All sessions are assigned to the registered Client
                          only and cannot be transferred, gifted, or sold.
                        </p>
                      </div>

                      {/* Section IV */}
                      <div data-id="element-238">
                        <h4 className="text-base font-heading text-accent-blue mb-3" data-id="element-239">
                          IV. CANCELLATION POLICY
                        </h4>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-240">
                          24-Hour Notice Requirement
                        </p>
                        <p data-id="element-241">
                          Cancellations or rescheduling must be made at least 24
                          hours in advance. Failure to provide sufficient notice
                          will result in the session being forfeited and
                          deducted from the package balance.
                        </p>
                      </div>

                      {/* Section V */}
                      <div data-id="element-242">
                        <h4 className="text-base font-heading text-accent-blue mb-3" data-id="element-243">
                          V. DATA PROTECTION (GDPR)
                        </h4>

                        <p className="mb-2" data-id="element-244">
                          I acknowledge that Aleks Szulc processes my personal
                          data (such as name and contact details) solely for the
                          purpose of providing training services, scheduling
                          sessions, and communication.
                        </p>
                        <p className="mb-2" data-id="element-245">I understand that:</p>
                        <ul className="list-disc list-inside space-y-1 ml-2" data-id="element-246">
                          <li data-id="element-247">
                            My data will not be shared with third parties
                            without my consent, unless required by law
                          </li>
                          <li data-id="element-248">
                            I have the right to access, correct, or request
                            deletion of my data
                          </li>
                          <li data-id="element-249">
                            I may withdraw consent at any time by contacting the
                            Coach
                          </li>
                        </ul>
                      </div>

                      {/* Section VII */}
                      <div data-id="element-250">
                        <h4 className="text-base font-heading text-accent-blue mb-3" data-id="element-251">
                          VII. GOVERNING LAW
                        </h4>
                        <p data-id="element-252">
                          This Agreement shall be governed by and interpreted in
                          accordance with the laws of Poland.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Waiver Form */}
                  <form onSubmit={handleWaiverSubmit} className="space-y-6" data-id="element-253">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-254">
                      <div data-id="element-255">
                        <label htmlFor="waiver-name" className="block text-sm font-body text-gray-400 mb-2" data-id="element-256">
                          Client Name
                        </label>
                        <input type="text" id="waiver-name" required value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Enter your full name" className="w-full bg-dark-primary border border-dark-border rounded-lg px-4 py-3 text-white font-body focus:outline-none focus:border-accent-blue transition-colors" data-id="element-257" />
                      </div>
                      <div data-id="element-258">
                        <label htmlFor="waiver-date" className="block text-sm font-body text-gray-400 mb-2" data-id="element-259">
                          Date
                        </label>
                        <input type="text" id="waiver-date" required placeholder="DD/MM/YYYY" value={clientDate} onChange={e => {
                      let val = e.target.value.replace(/[^\d]/g, '');
                      if (val.length > 8) val = val.slice(0, 8);
                      if (val.length >= 5) {
                        val = val.slice(0, 2) + '/' + val.slice(2, 4) + '/' + val.slice(4);
                      } else if (val.length >= 3) {
                        val = val.slice(0, 2) + '/' + val.slice(2);
                      }
                      setClientDate(val);
                    }} maxLength={10} className="w-full bg-dark-primary border border-dark-border rounded-lg px-4 py-3 text-white font-body focus:outline-none focus:border-accent-blue transition-colors" data-id="element-260" />
                      </div>
                    </div>

                    {/* VI. Media Release */}
                    <div className="bg-dark-elevated border border-dark-border rounded-xl p-5" data-id="element-261">
                      <h4 className="text-base font-heading text-accent-blue mb-2" data-id="element-262">
                        VI. MEDIA RELEASE (OPTIONAL)
                      </h4>
                      <p className="text-sm text-gray-400 font-body mb-4" data-id="element-263">
                        I grant permission for the Coach to take photos and
                        videos during training sessions and use them for
                        promotional purposes (e.g., social media, website),
                        unless I explicitly withdraw this consent in writing.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4" data-id="element-264">
                        <label className="flex items-center gap-3 cursor-pointer" data-id="element-265">
                          <input type="radio" name="media-consent" value="agree" checked={mediaConsent === 'agree'} onChange={() => setMediaConsent('agree')} className="w-5 h-5 accent-[#00b4ff]" data-id="element-266" />
                          <span className="text-white font-body font-medium" data-id="element-267">
                            I agree
                          </span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer" data-id="element-268">
                          <input type="radio" name="media-consent" value="disagree" checked={mediaConsent === 'disagree'} onChange={() => setMediaConsent('disagree')} className="w-5 h-5 accent-[#00b4ff]" data-id="element-269" />
                          <span className="text-white font-body font-medium" data-id="element-270">
                            I do NOT agree
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Acknowledgment */}
                    <div className="flex items-start gap-4" data-id="element-271">
                      <input type="checkbox" id="waiver-agree" required className="mt-1 w-5 h-5 rounded border-dark-border bg-dark-primary text-accent-blue focus:ring-accent-blue focus:ring-offset-0 cursor-pointer accent-[#00b4ff]" data-id="element-272" />
                      <label htmlFor="waiver-agree" className="text-sm text-gray-300 font-body cursor-pointer" data-id="element-273">
                        I HAVE READ THIS AGREEMENT, FULLY UNDERSTAND ITS TERMS,
                        AND VOLUNTARILY SIGN IT. (Parent/Guardian signature
                        required if under 18.)
                      </label>
                    </div>

                    <button type="submit" className="w-full bg-accent-blue text-black font-heading tracking-wider text-xl py-4 rounded-lg hover:bg-accent-blue-hover transition-colors" data-id="element-274">
                      SIGN AGREEMENT
                    </button>
                  </form>
                </>}
            </div>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-24" data-id="element-275">
          <h2 className="text-3xl font-heading text-white text-center mb-10" data-id="element-276">
            FREQUENTLY ASKED QUESTIONS
          </h2>
          <div className="max-w-3xl mx-auto space-y-3" data-id="element-277">
            {[{
            q: 'Do I need any prior experience?',
            a: "Not at all. Whether you're a complete beginner or an experienced fighter, sessions are tailored to your level and goals. The Trial Session is a great way to start."
          }, {
            q: 'What should I bring to a session?',
            a: 'Comfortable workout clothes, a water bottle, and a towel. If you have your own gloves and wraps, bring those too — otherwise, equipment can be provided for your first sessions.'
          }, {
            q: 'How long is each session?',
            a: 'Each training session lasts approximately 60 minutes, including warm-up, technique work, drills, and cool-down.'
          }, {
            q: 'Can I reschedule or cancel a session?',
            a: 'Yes, but cancellations or rescheduling must be made at least 24 hours in advance. Late cancellations will result in the session being forfeited from your package.'
          }, {
            q: 'Do session packages expire?',
            a: 'No. Purchased sessions do not expire and remain valid until used. Train at your own pace.'
          }, {
            q: 'How do group sessions work?',
            a: 'Group sessions (2–4 people) follow the same structure as solo sessions but are adapted for multiple participants. Everyone trains together under my guidance. Use the group size tabs above to see pricing, or try the Match & Strike Program if you need a training partner.'
          }, {
            q: 'Where do sessions take place?',
            a: 'Sessions are held at ul. Koński Jar 6, 02-785 Warszawa.'
          }, {
            q: 'What is the Match & Strike Program?',
            a: "It's a free matchmaking service for people who want affordable group training but don't have a training partner. Fill out the form and I'll pair you with someone who matches your goals, schedule, and experience level."
          }, {
            q: 'Do I need to sign a waiver?',
            a: "Yes. Before your first session, you'll need to read and sign the Training Agreement above. It covers liability, conduct, payment terms, and data protection."
          }].map((faq, idx) => <FaqItem key={idx} question={faq.q} answer={faq.a} data-id="element-278" />)}
          </div>
        </motion.div>

        {/* Location Footer */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="flex flex-col items-center justify-center py-10 border-t border-dark-border" data-id="element-279">
          <div className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors" data-id="element-280">
            <MapPin className="w-6 h-6 text-accent-blue" data-id="element-281" />
            <a href="https://maps.google.com/?q=ul.+Koński+Jar+6,+02-785+Warszawa" target="_blank" rel="noopener noreferrer" className="font-body text-lg" data-id="element-282">
              Location: ul. Koński Jar 6, 02-785 Warszawa
            </a>
          </div>
        </motion.div>
      </div>
    </main>;
}
function FaqItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden" data-id="element-283">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left" data-id="element-284">
        <span className="font-heading text-lg text-white" data-id="element-285">{question}</span>
        <motion.div animate={{
        rotate: open ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="shrink-0" data-id="element-286">
          <ChevronDown className="w-5 h-5 text-accent-blue" data-id="element-287" />
        </motion.div>
      </button>
      <AnimatePresence initial={false} data-id="element-288">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-289">
            <p className="px-6 pb-5 text-gray-400 font-body text-sm leading-relaxed" data-id="element-290">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}